import { Checkbox as MUICheckbox, CheckboxProps } from '@material-ui/core'
import React, { MutableRefObject, ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { IsStringEmpty, isVarNotEmpty, parseBool } from '../../../../NonReact/utils/GlobalFunctions'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext' // External imports.
import { ReactState, Rules } from '../../Types/Types'
import Label from '../Label'
import PopperBubble from '../PopperBubble/PopperBubble'
import { SPACE } from '../../Constants/Separators'

/**
 * Component properties type.
 */
export type Props = {
    onChange?: (checked: boolean) => void
    labelLeft?: string
    labelRight?: string
    isLabelHidden?: boolean
    disabled?: boolean
    id?: string
    fullWidth?: boolean
    title?: string
    name?: string
    defaultChecked?: boolean
    isChecked?: boolean
    rules?: Rules
    hideByDefault?: boolean
    parentHover?: boolean
    readOnly?: boolean
    className?: string
    popupHover?: ReactElement
    checkedIcon?: ReactElement
    icon?: ReactElement
}

/**
 * The Checkbox UI component.
 * @param onChange Function to callback to on toggle.
 * @param isChecked is a flag set to check whether the box is already checked.
 * @param labelLeft Left label of the checkbox.
 * @param isLabelHidden Whether the label is visually hidden.
 * @param name Name of the checkbox.
 * @param hideByDefault hide checkbox by default.
 * @param parentHover hovering parent.
 */
const Checkbox: React.FC<Props> = ({
    onChange,
    labelLeft,
    labelRight,
    isLabelHidden,
    disabled,
    id,
    fullWidth,
    title,
    name: checkboxName,
    isChecked,
    rules,
    hideByDefault,
    parentHover,
    readOnly,
    className,
    popupHover,
    checkedIcon,
    icon
}: Props): ReactElement => {
    let methods: Definable<UseFormMethods<Record<string, unknown>>>

    if (checkboxName) {
        methods = useFormContext()
    }

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const hideClass: string = hideByDefault ? 'invisible' : ''

    const anchorEl: MutableRefObject<null> = useRef<null>(null)
    const [popperOpen, setPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        event.stopPropagation()

        if (onChange) {
            onChange(event.target.checked)
        }
    }

    const labelClickHandler = useCallback(
        (e?: React.MouseEvent): void => {
            e?.preventDefault()
            e?.stopPropagation()

            if (!disabled) {
                if (methods && checkboxName) {
                    const { setValue, getValues }: UseFormMethods = methods
                    const checked: boolean = getValues(checkboxName)

                    setValue(checkboxName, !checked)
                    onChange?.(checked)
                } else {
                    onChange?.(!isChecked)
                }
            }
        },
        [onChange, isChecked, disabled, methods, checkboxName]
    )

    const inputProps: CheckboxProps = useMemo(
        (): CheckboxProps => ({
            inputProps: {
                'aria-label': 'primary checkbox',
                disabled: readOnly
            },
            disabled: !!disabled,
            id: checkboxName ?? id,
            readOnly,
            ref: anchorEl
        }),
        [readOnly, disabled, checkboxName, id]
    )

    const labelList: string[] = useMemo(
        (): string[] => [
            labelRight ? 'label-right' : 'label-left',
            isLabelHidden ? 'visually-hidden' : '',
            fullWidth ? 'full-width' : '',
            !disabled ? 'clickable-label' : ''
        ],
        [labelRight, isLabelHidden, fullWidth, disabled]
    )

    const labelClass: string = useMemo(
        (): string => labelList.filter((label: string): boolean => !IsStringEmpty(label)).join(SPACE),
        [labelList]
    )

    const controlledCheckbox = useCallback((): ReactElement => {
        if (checkboxName && methods) {
            return (
                <Controller
                    name={checkboxName}
                    control={methods.control}
                    rules={rules}
                    defaultValue={parseBool(methods.watch(checkboxName) ?? 'false')}
                    render={({ value, name, ref }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                        <MUICheckbox
                            name={name}
                            id={name ?? id}
                            checked={Boolean(value)}
                            inputRef={ref}
                            checkedIcon={checkedIcon}
                            icon={icon}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                if (commonForm?.handleFormChange) {
                                    commonForm?.handleFormChange(name)
                                }
                                methods?.setValue(name, event.target.checked)
                                handleChange(event)
                            }}
                            {...inputProps}
                        />
                    )}
                />
            )
        }

        return <React.Fragment />
    }, [checkboxName, methods, rules, commonForm, checkedIcon, icon, inputProps, handleChange])

    const uncontrolledCheckbox = useCallback(
        (): ReactElement => (
            <MUICheckbox
                checked={isChecked}
                onChange={handleChange}
                {...inputProps}
                checkedIcon={checkedIcon}
                icon={icon}
            />
        ),
        [isChecked, handleChange, inputProps, checkedIcon, icon]
    )

    return (
        <>
            <div
                className={`checkbox ${readOnly ? 'read-only' : ''} ${fullWidth ? 'full-width' : ''} ${
                    isChecked || parentHover ? '' : hideClass
                } ${className ?? ''}`}
                onMouseOver={(): void => {
                    if (popupHover) {
                        setPopperOpen(true)
                    }
                }}
                onMouseOut={(): void => {
                    if (popupHover) {
                        setPopperOpen(false)
                    }
                }}
            >
                {labelLeft && (
                    <Label
                        customClass={labelClass}
                        htmlFor={checkboxName || id}
                        title={title}
                        onClick={labelClickHandler}
                    >
                        {labelLeft}
                    </Label>
                )}
                {checkboxName ? controlledCheckbox() : uncontrolledCheckbox()}
                {labelRight && (
                    <Label
                        customClass={labelClass}
                        htmlFor={checkboxName || id}
                        title={title}
                        onClick={labelClickHandler}
                    >
                        {labelRight}
                    </Label>
                )}
            </div>
            {isVarNotEmpty(popupHover) ? (
                <PopperBubble
                    anchorEl={anchorEl.current}
                    content={popupHover ?? <></>}
                    open={popperOpen}
                    placement='top'
                    infoMode
                />
            ) : (
                <></>
            )}
        </>
    )
}

export default Checkbox
